// src/api/verificacionApi.js
//
// Verificación documental para HolaVeci (portado del sistema Ecolab/Fenagro).
// Endpoint protegido con rol SUPERADMIN.
//
// Flujo backend: lectura PDF417/QR → OCR Google Vision → cruce estricto
// contra nombre/apellidos digitados. Ver Tracker.Api/Controllers/HolaVeci/
// VerificadorDocumentoController.cs.

import { API_BASE_URL, tokenStorage, manejarNoAutorizado } from './apiClient';

/**
 * Verifica un documento subiendo el archivo y cruzando datos del formulario.
 *
 * @param {File}   archivo       Archivo (PDF, JPG, JPEG, PNG). Máx 15MB.
 * @param {string} cedula        Número de documento a cotejar
 * @param {string} nombres       Nombres a cotejar
 * @param {string} apellidos     Apellidos a cotejar
 * @param {string} tipoDocumento cedula | rut | antecedentes_procuraduria |
 *                               antecedentes_contraloria | antecedentes_policia |
 *                               bancario | generico
 * @returns Objeto ResultadoVerificacion: {
 *   valido: bool, confianza: 0-100, confianzaMinima, mensaje,
 *   pasos: [{ paso, estado, detalle, datos }],
 *   codigoBarrasLeido, datosCodigoBarras[], textoExtraido,
 *   cedulaExtraida, cruceExitoso, urlDocumento, mensajeAdicional
 * }
 */
export async function verificarDocumento(
  archivo, cedula = '', nombres = '', apellidos = '', tipoDocumento = 'cedula'
) {
  const formData = new FormData();
  formData.append('archivo',       archivo);
  formData.append('tipoDocumento', tipoDocumento);
  if (cedula)    formData.append('cedula',    cedula);
  if (nombres)   formData.append('nombres',   nombres);
  if (apellidos) formData.append('apellidos', apellidos);

  const res = await fetch(`${API_BASE_URL}/api/holaveci/verificar-documento`, {
    method: 'POST',
    body: formData,
    headers: { Authorization: `Bearer ${tokenStorage.get()}` },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Esta llamada usa fetch crudo (multipart/FormData, no encaja en el
    // wrapper JSON de apiClient) y por eso NUNCA pasaba por el manejo de
    // 401 del resto de la app — un token expirado (dura 30 min) se veía
    // como un error de verificación genérico, no como "tu sesión expiró".
    if (res.status === 401) manejarNoAutorizado();
    throw Object.assign(new Error(data.mensaje || 'Error verificando documento'),
      { data, status: res.status });
  }
  return data;
}
